import React, { useState, useEffect } from 'react';
import { MdArrowBack, MdReportProblem, MdCheckCircle, MdChat, MdCallSplit } from 'react-icons/md';
import { useApp } from '../contexts/AppContext';
import { authHeaders } from '../services/backendAPI';

const CATEGORY_ICONS = {
  'Groceries': '🛒', 'Food & Dining': '🍽️', 'Restaurant': '🍽️',
  'Gas': '⛽', 'Auto': '🚗', 'Auto / Maintenance': '🔧',
  'Travel': '✈️', 'Entertainment': '🎬', 'Shopping': '🛍️',
  'Utilities': '💡', 'Health': '🏥', 'Pharmacy': '💊',
  'Bank Fee': '🏦', 'Streaming': '📺', 'Phone': '📱',
  'Internet': '🌐', 'Insurance': '🛡️', 'Kids Activities': '🎈',
  'Mortgage': '🏠', 'Condo Fees': '🏢', 'Property Tax': '🏛️',
  'Public Transit': '🚌', 'Cash': '💵', 'Credit Card Payment': '💳',
  'Employment Income': '💰', 'Government Benefit': '🏛️',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${months[parseInt(m,10)-1]} ${parseInt(d,10)}, ${y}`;
}

async function fetchInterpretation(txn) {
  const flagContext = txn.reason === 'new-merchant'
    ? `This is the first time this merchant has appeared in the customer's transaction history.`
    : `This charge is ${txn.multiple}× above the customer's typical ${txn.category} spend of ~$${txn.median}.`;

  const prompt = `You are a helpful banking assistant. In 2–3 short sentences, explain what this transaction likely is, who the merchant probably is, and briefly note why it was flagged as unusual. Be specific and helpful, not alarming. Do not include markdown.

Transaction: ${txn.description}
Amount: -$${txn.amount?.toFixed(2)}
Category: ${txn.category}
Date: ${txn.date}
Flag reason: ${flagContext}`;

  try {
    const res = await fetch('/api/summarize', {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        text: prompt,
        summarizePrompt: prompt,
        summarizeProvider: 'auto',
      }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.tldr || null;
  } catch {
    return null;
  }
}

const TransactionDetailScreen = ({ onNavigate, onBack, transaction, onFraudReport, onChatHandoff }) => {
  const { theme } = useApp();
  const isLight = theme !== 'purple';

  const [interpretation, setInterpretation] = useState(null);
  const [interpLoading, setInterpLoading] = useState(true);
  const [fraudSubmitting, setFraudSubmitting] = useState(false);
  const [fraudCase, setFraudCase] = useState(null); // { caseId, submittedAt } after successful submission
  const [fraudStatus, setFraudStatus] = useState(() => {
    if (!transaction) return null;
    try {
      const reports = JSON.parse(localStorage.getItem('iva_fraud_reports') || '{}');
      return reports[`${transaction.date}|${transaction.description}`] || null;
    } catch { return null; }
  });

  useEffect(() => {
    if (!transaction) return;
    setInterpLoading(true);
    fetchInterpretation(transaction).then(text => {
      setInterpretation(text);
      setInterpLoading(false);
    });
  }, [transaction]);

  if (!transaction) {
    return (
      <div className="screen bh-flow">
        <header className="bh-flow-hero">
          <div className="bh-flow-hero-left">
            <button className="bh-flow-back" onClick={onBack} aria-label="Back"><MdArrowBack /></button>
            <h1 className="bh-flow-title">Transaction Details</h1>
          </div>
        </header>
        <div className="bh-flow-body" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No transaction selected.</div>
      </div>
    );
  }

  const isDebit = transaction.amount > 0; // anomaly amounts are already positive
  const amtColor = isLight ? '#c1440e' : 'rgba(255,150,90,0.95)';
  const catIcon = CATEGORY_ICONS[transaction.category] || '💳';

  const markFraud = async (status) => {
    const key = `${transaction.date}|${transaction.description}`;
    try {
      const reports = JSON.parse(localStorage.getItem('iva_fraud_reports') || '{}');
      reports[key] = status;
      localStorage.setItem('iva_fraud_reports', JSON.stringify(reports));
    } catch {}
    setFraudStatus(status);
    if (onFraudReport) onFraudReport(key, status);

    if (status === 'fraud') {
      setFraudSubmitting(true);
      try {
        const res = await fetch('/api/fraud/report', {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ transaction }),
        });
        if (res.ok) {
          const data = await res.json();
          setFraudCase({ caseId: data.caseId, submittedAt: data.submittedAt });
        }
      } catch {}
      setFraudSubmitting(false);
    }
  };

  const askIVA = () => {
    const msg = transaction.reason === 'new-merchant'
      ? `Tell me about the ${transaction.description} charge of $${transaction.amount.toFixed(2)} on ${transaction.date}. I don't recognize this merchant.`
      : `Tell me about the ${transaction.description} charge of $${transaction.amount.toFixed(2)} on ${transaction.date}. It seems higher than usual.`;
    if (onChatHandoff) onChatHandoff(msg);
    else onNavigate('chat');
  };

  // Split-the-bill (plan 240): a dining/bar charge big enough to plausibly have
  // covered more than one person can be split — IVA offers to request the other
  // person's share. Framed as a question, never a claim.
  const DINING_RE = /dining|restaurant|food|bar|pub|cafe|grill|kitchen|bistro|tavern|eatery|brewery|steakhouse/i;
  const looksShareable = DINING_RE.test(`${transaction.category || ''} ${transaction.description || ''}`)
    && Number(transaction.amount) > 40;
  const half = Math.round((Number(transaction.amount) || 0) / 2 * 100) / 100;
  const splitBill = () => {
    const msg = `Was the $${Number(transaction.amount).toFixed(2)} charge at ${transaction.description} on ${transaction.date} shared with someone? If so, help me request their half ($${half.toFixed(2)}) via e-Transfer.`;
    if (onChatHandoff) onChatHandoff(msg);
    else onNavigate('chat');
  };

  // Style tokens
  const bg      = isLight ? '#f4f6f8' : 'var(--bg-screen)';
  const cardBg  = isLight ? '#fff' : 'rgba(255,255,255,0.05)';
  const cardBdr = isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.08)';
  const labelClr = isLight ? '#888' : 'rgba(160,210,255,0.5)';
  const valClr   = isLight ? '#111' : 'rgba(220,235,255,0.9)';
  const divClr   = isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.07)';
  const heroText = isLight ? '#111' : 'rgba(220,235,255,0.95)';
  const subText  = isLight ? '#666' : 'rgba(160,210,255,0.6)';

  const Card = ({ children, style }) => (
    <div style={{ background: cardBg, border: `1px solid ${cardBdr}`, borderRadius: 14, margin: '0 16px 12px', overflow: 'hidden', flexShrink: 0, ...style }}>
      {children}
    </div>
  );

  const Row = ({ label, value, valueStyle }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', borderBottom: `1px solid ${divClr}` }}>
      <span style={{ fontSize: 13, color: labelClr }}>{label}</span>
      <span style={{ fontSize: 13, color: valClr, textAlign: 'right', maxWidth: '65%', ...valueStyle }}>{value}</span>
    </div>
  );

  const flagColor = transaction.reason === 'new-merchant' ? '#c1440e' : 'rgba(255,170,60,0.9)';
  const flagBg    = isLight ? 'rgba(255,120,60,0.07)' : 'rgba(255,120,60,0.12)';

  return (
    <div className="screen bh-flow" style={{ background: bg }}>
      {/* Header */}
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack} aria-label="Back"><MdArrowBack /></button>
          <h1 className="bh-flow-title">Transaction Details</h1>
        </div>
      </header>

      <div className="bh-flow-body">

        {/* Hero amount block */}
        <div style={{ textAlign: 'center', padding: '28px 24px 24px', borderBottom: `1px solid ${divClr}`, marginBottom: 16, flexShrink: 0 }}>
          <div style={{ fontSize: 13, color: subText, marginBottom: 6 }}>{catIcon} {transaction.category}</div>
          <div style={{ fontSize: 36, fontWeight: 800, color: amtColor, letterSpacing: '-0.5px', marginBottom: 6 }}>
            −${transaction.amount.toFixed(2)}
          </div>
          <div style={{ fontSize: 17, fontWeight: 600, color: heroText, marginBottom: 4 }}>{transaction.description}</div>
          <div style={{ fontSize: 12, color: subText }}>{formatDate(transaction.date)}</div>

          {/* Fraud status badge */}
          {fraudStatus && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12,
              padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
              background: fraudStatus === 'fraud' ? 'rgba(200,0,0,0.12)' : (isLight ? 'rgba(27,122,61,0.10)' : 'rgba(60,200,100,0.12)'),
              color: fraudStatus === 'fraud' ? '#c00' : (isLight ? '#1b7a3d' : 'rgba(90,220,140,0.95)'),
              border: `1px solid ${fraudStatus === 'fraud' ? 'rgba(200,0,0,0.25)' : 'rgba(60,200,100,0.3)'}`,
            }}>
              {fraudStatus === 'fraud' ? <MdReportProblem size={14}/> : <MdCheckCircle size={14}/>}
              {fraudStatus === 'fraud' ? 'Reported as fraud' : 'Confirmed legitimate'}
            </div>
          )}
        </div>

        {/* Details card */}
        <Card>
          <Row label="Date" value={formatDate(transaction.date)} />
          <Row label="Category" value={`${catIcon} ${transaction.category}`} />
          {transaction.account && <Row label="Account" value={transaction.account} />}
          {transaction.reason === 'amount-outlier' && (
            <Row label="Typical spend" value={`~$${transaction.median} in ${transaction.category}`} />
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px' }}>
            <span style={{ fontSize: 13, color: labelClr }}>Status</span>
            <span style={{
              fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
              background: flagBg, color: flagColor, border: `1px solid ${flagColor}44`,
            }}>
              ⚠ Flagged by IVA
            </span>
          </div>
        </Card>

        {/* Why flagged */}
        <Card style={{ borderLeft: `3px solid ${flagColor}` }}>
          <div style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: flagColor, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              Why IVA flagged this
            </div>
            <div style={{ fontSize: 13, color: valClr, lineHeight: 1.55 }}>
              {transaction.reason === 'new-merchant'
                ? `This merchant has never appeared in your transaction history before. First-time merchants can sometimes indicate unauthorized use of your card.`
                : `This charge is ${transaction.multiple}× your typical ${transaction.category} spend (~$${transaction.median}). Large deviations from your normal patterns are worth reviewing.`}
            </div>
          </div>
        </Card>

        {/* IVA Interpretation */}
        <Card>
          <div style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: isLight ? '#1b5e20' : 'rgba(160,210,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              ✨ IVA's Take
            </div>
            {interpLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[80, 60, 45].map((w, i) => (
                  <div key={i} style={{ height: 12, borderRadius: 6, background: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)', width: `${w}%`, animation: 'pulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            ) : interpretation ? (
              <div style={{ fontSize: 13, color: valClr, lineHeight: 1.6 }}>{interpretation}</div>
            ) : (
              <div style={{ fontSize: 13, color: subText, fontStyle: 'italic' }}>Could not load interpretation — check your connection.</div>
            )}
          </div>
        </Card>

        {/* Fraud submitted confirmation */}
        {fraudCase && (
          <Card style={{ borderLeft: '3px solid #c1440e', flexShrink: 0 }}>
            <div style={{ padding: '16px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#c1440e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Fraud Report Submitted
              </div>
              <div style={{ fontSize: 13, color: valClr, lineHeight: 1.6, marginBottom: 12 }}>
                Your report has been sent to TD's fraud team. A specialist will review your case and may contact you within 3–5 business days.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: labelClr }}>Case number</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: valClr, fontFamily: 'monospace' }}>{fraudCase.caseId}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: labelClr }}>Submitted</span>
                  <span style={{ fontSize: 12, color: valClr }}>{new Date(fraudCase.submittedAt).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Actions */}
        <div style={{ padding: '4px 16px 32px', display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
          {fraudStatus !== 'fraud' && (
            <button
              onClick={() => markFraud('fraud')}
              disabled={fraudSubmitting}
              style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: '#c1440e', color: '#fff', fontSize: 15, fontWeight: 700, cursor: fraudSubmitting ? 'default' : 'pointer', opacity: fraudSubmitting ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <MdReportProblem size={18} /> {fraudSubmitting ? 'Submitting…' : 'Report as Fraud'}
            </button>
          )}
          {fraudStatus !== 'ok' && !fraudCase && (
            <button
              onClick={() => markFraud('ok')}
              style={{ width: '100%', padding: '14px', borderRadius: 12, border: `1px solid ${isLight ? 'rgba(27,122,61,0.35)' : 'rgba(60,200,100,0.35)'}`, background: isLight ? 'rgba(27,122,61,0.07)' : 'rgba(60,200,100,0.10)', color: isLight ? '#1b7a3d' : 'rgba(90,220,140,0.95)', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <MdCheckCircle size={18} /> Not Fraud — Looks Fine
            </button>
          )}
          {looksShareable && (
            <button
              onClick={splitBill}
              style={{ width: '100%', padding: '14px', borderRadius: 12, border: `1px solid ${isLight ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'}`, background: 'transparent', color: isLight ? '#333' : 'rgba(200,220,255,0.75)', fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <MdCallSplit size={18} /> Split this? Request ${half.toFixed(2)}
            </button>
          )}
          <button
            onClick={askIVA}
            style={{ width: '100%', padding: '14px', borderRadius: 12, border: `1px solid ${isLight ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'}`, background: 'transparent', color: isLight ? '#333' : 'rgba(200,220,255,0.75)', fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <MdChat size={18} /> Ask IVA About This
          </button>
          {fraudStatus && !fraudCase && (
            <button
              onClick={() => {
                const key = `${transaction.date}|${transaction.description}`;
                try {
                  const reports = JSON.parse(localStorage.getItem('iva_fraud_reports') || '{}');
                  delete reports[key];
                  localStorage.setItem('iva_fraud_reports', JSON.stringify(reports));
                } catch {}
                setFraudStatus(null);
              }}
              style={{ width: '100%', padding: '10px', borderRadius: 12, border: 'none', background: 'none', color: subText, fontSize: 13, cursor: 'pointer' }}
            >
              Undo report
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailScreen;
